import {
  IAllStatusInclAll,
  IApiPaginationResponse,
  IInternalOrder,
  IOrder,
  IOrderFilters,
  IOrderStatus,
  IOrderWithStatus,
  IPreorder,
  IPreorderStatus,
} from 'my-poppies-b2b-types';
import { sleep } from 'sleep-lightweight';

import { dummyDataOrdersBeingPrepared } from '@/dummyData/dummyDataOrdersBeingPrepared';
import { dummyDataOrdersCompleted } from '@/dummyData/dummyDataOrdersCompleted';
import { dummyDataOrdersDeclined } from '@/dummyData/dummyDataOrdersDeclined';
import { dummyDataOrdersDelivered } from '@/dummyData/dummyDataOrdersDelivered';
import { dummyDataOrdersFlawed } from '@/dummyData/dummyDataOrdersFlawed';
import { dummyDataOrdersNew } from '@/dummyData/dummyDataOrdersNew';
import { dummyDataOrdersShipped } from '@/dummyData/dummyDataOrdersShipped';
import { dummyDataPreorders } from '@/dummyData/dummyDataPreorders';
import { dummyDataPreordersCompleted } from '@/dummyData/dummyDataPreordersCompleted';
import { IOrderSorting } from '@/types/IOrderSorting';
import { getObjectEntries } from '@/utils/getObjectEntries';
import { sanitizeFilters } from '@/utils/sanitizeFilters';
import { sortArrayByProperty } from '@/utils/sortArrayByProperty';

const statusMappingOpenOrders: Omit<
  Record<IOrderStatus, IOrderWithStatus<IOrderStatus>[]>,
  'DELIVERED' | 'INTERNAL_ORDER_COMPLETED'
> = {
  NEW: dummyDataOrdersNew as IOrderWithStatus<IOrderStatus>[],
  DECLINED: dummyDataOrdersDeclined as IOrderWithStatus<IOrderStatus>[],
  BEING_PREPARED: dummyDataOrdersBeingPrepared,
  SHIPPED: dummyDataOrdersShipped,
  FLAWED: dummyDataOrdersFlawed,
};

const statusMappingOrders: Record<IOrderStatus, (IOrder | IInternalOrder)[]> = {
  ...statusMappingOpenOrders,
  DELIVERED: dummyDataOrdersDelivered,
  INTERNAL_ORDER_COMPLETED: dummyDataOrdersCompleted,
};

const statusMappingOpenPreorders: Omit<Record<IPreorderStatus, IPreorder[]>, 'PREORDER_COMPLETED'> = {
  PREORDER: [...dummyDataPreorders],
};

const statusMappingPreorders: Record<IPreorderStatus, IPreorder[]> = {
  ...statusMappingOpenPreorders,
  PREORDER_COMPLETED: dummyDataPreordersCompleted,
};

const statusMapping = { ...statusMappingOrders, ...statusMappingPreorders };

export const getMultipliedOrders = (orderStatus: IAllStatusInclAll) => {
  const chosenOrders =
    orderStatus === 'ALL_ORDERS'
      ? Object.values(statusMappingOrders).flat()
      : orderStatus === 'ALL_OPEN_ORDERS'
        ? Object.values(statusMappingOpenOrders).flat()
        : orderStatus === 'ALL_PREORDERS'
          ? Object.values(statusMappingPreorders).flat()
          : orderStatus === 'ALL_OPEN_PREORDERS'
            ? Object.values(statusMappingOpenPreorders).flat()
            : statusMapping[orderStatus];
  return new Array(50)
    .fill(chosenOrders)
    .flat()
    .map((dummyDataNewOrder, index) => ({ ...dummyDataNewOrder, id: `${dummyDataNewOrder.id}-${index}` }));
};

export const mockApiCallForOrders = async (
  orderStatus: IAllStatusInclAll,
  page: number,
  pageSize: number,
  filters: IOrderFilters,
  sorting: IOrderSorting
): Promise<IApiPaginationResponse<IOrder>> => {
  await sleep(250);
  const offset = page * pageSize;
  const multipliedOrders = getMultipliedOrders(orderStatus);
  const countTotal = multipliedOrders.length;
  let filteredOrders = multipliedOrders;
  getObjectEntries(sanitizeFilters(filters)).forEach(([propertyToFilter, filterSetting]) => {
    if (filterSetting === undefined) {
      return;
    }
    if (propertyToFilter === 'positionId') {
      filteredOrders = filteredOrders.filter((order: IOrder) =>
        (order?.positions ?? []).map((p) => p.positionId).includes(filterSetting)
      );
    } else {
      filteredOrders = filteredOrders.filter((order) => order[propertyToFilter] === filterSetting);
    }
  });
  const sortedOrders = !sorting
    ? filteredOrders
    : sortArrayByProperty(filteredOrders, sorting.column, sorting.direction);
  const ordersOnCurrentPage = sortedOrders.slice(offset, offset + pageSize);
  return {
    itemsOnPage: ordersOnCurrentPage,
    countOnPage: ordersOnCurrentPage.length,
    countTotalForFilters: filteredOrders.length,
    countTotal,
    pageCountTotal: 123, // random since ignored anyhow for now
    pageCountTotalForFilters: 12, // random since ignored anyhow for now
  };
};
